import EmployeeSearchPicker from '../components/EmployeeSearchPicker';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Table, 
  Form, 
  Button, 
  Alert, 
  Spinner, 
  Pagination, 
  Badge, 
  Tabs, 
  Tab,
  Modal
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import ImageWithDetectionBox from '../components/ImageWithDetectionBox';
import ScopeIndicator from '../components/ScopeIndicator';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { ppeViolationsAPI, departmentAPI, faceImageAPI, isRequestCanceled } from '../utils/api';
import { useListRequest } from '../hooks/useListRequest';
import type { 
  PPEViolation, 
  PPEViolationSummary, 
  PPEMonthlySummaryRow 
} from '../types/ppe';
import type { Department } from '../types/department';
import { getTodayDateInCompanyTimezone, formatDateTimeInCompanyTimezone, getCurrentMonthInCompanyTimezone } from '../utils/timezone';
import { resolvePpeImagePathSegments } from '../utils/ppeImagePath';
import ViolationClipModal from '../components/ViolationClipModal';

const isDev = import.meta.env.DEV;
const logError = (message: string, ...args: any[]) => {
  if (isDev) console.error(message, ...args);
};

const COOLDOWN_MS = 5 * 60 * 1000;

function applyCooldown(violations: PPEViolation[]): PPEViolation[] {
  const lastKept = new Map<string, number>();
  return violations.filter(v => {
    const ms = new Date(v.log_time).getTime();
    const prev = lastKept.get(v.employee_id);
    if (prev !== undefined && Math.abs(ms - prev) < COOLDOWN_MS) return false;
    lastKept.set(v.employee_id, ms);
    return true;
  });
}

const PPE_TYPE_FILTER_KEYS = ['hairnet', 'gloves', 'helmet', 'industrial_gloves', 'industrial_jacket', 'unauthorized'] as const;

interface ViolationsPaginationMeta {
  current_page: number;
  last_page: number;
  total: number;
  per_page: number;
}

const ViolationsListPagination: React.FC<{
  meta: ViolationsPaginationMeta;
  onPageChange: (page: number) => void;
  showingLabel: string;
}> = ({ meta, onPageChange, showingLabel }) => {
  if (meta.last_page <= 1) return null;

  const from = (meta.current_page - 1) * meta.per_page + 1;
  const to = Math.min(meta.current_page * meta.per_page, meta.total);

  return (
    <div className="d-flex justify-content-between align-items-center p-3 border-top flex-wrap gap-2">
      <small className="text-muted">
        {showingLabel.replace('{{from}}', String(from)).replace('{{to}}', String(to)).replace('{{total}}', String(meta.total))}
      </small>
      <Pagination className="mb-0 an-pagination">
        <Pagination.First onClick={() => onPageChange(1)} disabled={meta.current_page === 1} />
        <Pagination.Prev onClick={() => onPageChange(meta.current_page - 1)} disabled={meta.current_page === 1} />
        {[...Array(Math.min(5, meta.last_page))].map((_, i) => {
          let pageNum: number;
          if (meta.last_page <= 5) pageNum = i + 1;
          else if (meta.current_page <= 3) pageNum = i + 1;
          else if (meta.current_page >= meta.last_page - 2) pageNum = meta.last_page - 4 + i;
          else pageNum = meta.current_page - 2 + i;

          return (
            <Pagination.Item
              key={pageNum}
              active={pageNum === meta.current_page}
              onClick={() => onPageChange(pageNum)}
            >
              {pageNum}
            </Pagination.Item>
          );
        })}
        <Pagination.Next
          onClick={() => onPageChange(meta.current_page + 1)}
          disabled={meta.current_page === meta.last_page}
        />
        <Pagination.Last
          onClick={() => onPageChange(meta.last_page)}
          disabled={meta.current_page === meta.last_page}
        />
      </Pagination>
    </div>
  );
};


const PPEViolations: React.FC = () => {
  const { currentCompany, userRolePermissionsLoading } = useAuth();
  const { canAccessPpeViolations } = useRolePermissions();
  const { t } = useTranslation('pages', { keyPrefix: 'ppeViolations' });
  const { t: tc } = useTranslation('common');

  const ppeTypeLabels = useMemo<Record<string, string>>(() => ({
    hairnet: tc('ppeTypes.hairnet'),
    gloves: tc('ppeTypes.gloves'),
    helmet: tc('ppeTypes.helmet'),
    industrial_gloves: tc('ppeTypes.indGloves'),
    industrial_jacket: tc('ppeTypes.indJacket'),
    unauthorized: tc('ppeTypes.unauthorized'),
    total: tc('ppeTypes.total'),
  }), [tc]);

  const ppeTypeFilterOptions = useMemo<Record<string, string>>(() =>
    Object.fromEntries(PPE_TYPE_FILTER_KEYS.map(k => [k, ppeTypeLabels[k] || k])),
    [ppeTypeLabels]
  );

  const violationCategoryLabels = useMemo<Record<string, string>>(() => ({
    missing: tc('ppeTypes.missing'),
    unauthorized: tc('ppeTypes.unauthorized'),
  }), [tc]);

  const companyTimezone = currentCompany?.timezone || 'UTC';

  const [activeTab, setActiveTab] = useState<string>('daily');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ppeDisabled, setPpeDisabled] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);

  const [dailyData, setDailyData] = useState<{
    summary: PPEViolationSummary;
    violations: PPEViolation[];
  }>({
    summary: { total: 0 },
    violations: []
  });
  const [dailyMeta, setDailyMeta] = useState({ current_page: 1, last_page: 1, total: 0, per_page: 20 });
  const [dailyFilters, setDailyFilters] = useState({
    date: getTodayDateInCompanyTimezone(companyTimezone),
    department_id: '',
    employee_id: '',
    violation_type: '',
    page: 1
  });
  const [dailyFormState, setDailyFormState] = useState({ ...dailyFilters });

  const [monthlyData, setMonthlyData] = useState<PPEMonthlySummaryRow[]>([]);
  const [monthlyMeta, setMonthlyMeta] = useState({ current_page: 1, last_page: 1, total: 0, per_page: 20 });
  const [monthlyFilters, setMonthlyFilters] = useState(() => {
    const storedCompany = localStorage.getItem('currentCompany');
    let initialMonth = new Date().toISOString().slice(0, 7);
    if (storedCompany) {
      try {
        const parsed = JSON.parse(storedCompany);
        if (parsed.timezone) {
          initialMonth = getTodayDateInCompanyTimezone(parsed.timezone).slice(0, 7);
        }
      } catch (e) {}
    }
    return {
      month: initialMonth,
      department_id: '',
      page: 1
    };
  });
  const [monthlyFormState, setMonthlyFormState] = useState({ ...monthlyFilters });

  const [showClipModal, setShowClipModal] = useState(false);
  const [clipViolation, setClipViolation] = useState<PPEViolation | null>(null);
  const [cooldownEnabled, setCooldownEnabled] = useState(true);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState<PPEViolation | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<{ id: string; name: string; emp_id: string } | null>(null);
  const [historyData, setHistoryData] = useState<{
    summary: PPEViolationSummary;
    violations: PPEViolation[];
  }>({
    summary: { total: 0 },
    violations: []
  });
  const [historyMeta, setHistoryMeta] = useState({ current_page: 1, last_page: 1, total: 0, per_page: 10 });
  const [historyFilters, setHistoryFilters] = useState({
    violation_type: '',
    date_from: '',
    date_to: '',
    page: 1
  });
  const [historyLoading, setHistoryLoading] = useState(false);

  const currentCompanyRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);

  // One shared signal slot for the daily/monthly list so switching tabs or filters
  // cancels the superseded request; the history modal owns a separate slot.
  const { begin: beginList } = useListRequest(String(currentCompany?.company_id));
  const { begin: beginHistory } = useListRequest(String(currentCompany?.company_id));

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (currentCompany?.company_id) {
      const today = getTodayDateInCompanyTimezone(currentCompany.timezone || 'UTC');
      const initialDailyFilters = {
        date: today,
        department_id: '',
        employee_id: '',
        violation_type: '',
        page: 1
      };
      const initialMonthlyFilters = {
        month: getCurrentMonthInCompanyTimezone(currentCompany.timezone || 'UTC'),
        department_id: '',
        page: 1
      };

      setPpeDisabled(false);
      setError(null);
      setDailyFilters(initialDailyFilters);
      setDailyFormState(initialDailyFilters);
      setMonthlyFilters(initialMonthlyFilters);
      setMonthlyFormState(initialMonthlyFilters);
      setDailyData({
        summary: { total: 0 },
        violations: []
      });
      setMonthlyData([]);
      setShowHistoryModal(false);
      setSelectedEmployee(null);
    }
  }, [currentCompany]);

  const loadDropdownData = useCallback(async () => {
    if (!currentCompany?.company_id) return;
    const requestCompanyId = currentCompany.company_id;
    currentCompanyRef.current = requestCompanyId;

    try {
      if (!isMountedRef.current) return;
      setLoadingDropdowns(true);
      const depsRes = await departmentAPI.getDepartments(requestCompanyId);
      if (isMountedRef.current && currentCompanyRef.current === requestCompanyId) {
        setDepartments(depsRes.departments || []);
      }
    } catch (err) {
      logError('Error loading dropdown data:', err);
    } finally {
      if (isMountedRef.current && currentCompanyRef.current === requestCompanyId) {
        setLoadingDropdowns(false);
      }
    }
  }, [currentCompany]);

  const loadDailyViolations = useCallback(async () => {
    if (!currentCompany || !canAccessPpeViolations || activeTab !== 'daily') return;
    const requestCompanyId = currentCompany.company_id;
    currentCompanyRef.current = requestCompanyId;
    const controller = beginList();

    try {
      if (!isMountedRef.current) return;
      setLoading(true);
      setError(null);
      const response = await ppeViolationsAPI.getDailyViolations(requestCompanyId, dailyFilters, controller.signal);
      if (controller.signal.aborted) return;
      if (isMountedRef.current && currentCompanyRef.current === requestCompanyId) {
        setDailyData(response.data);
        setDailyMeta(response.meta);
      }
    } catch (err: any) {
      if (controller.signal.aborted || isRequestCanceled(err)) return;
      if (isMountedRef.current && currentCompanyRef.current === requestCompanyId) {
        if (err.response?.status === 403) setPpeDisabled(true);
        else setError(err.response?.data?.message || err.message || t('loadDailyFailed'));
      }
    } finally {
      if (!controller.signal.aborted && isMountedRef.current && currentCompanyRef.current === requestCompanyId) {
        setLoading(false);
      }
    }
  }, [currentCompany, activeTab, dailyFilters, canAccessPpeViolations, t, beginList]);

  const loadMonthlySummary = useCallback(async () => {
    if (!currentCompany || !canAccessPpeViolations || activeTab !== 'monthly') return;
    const requestCompanyId = currentCompany.company_id;
    currentCompanyRef.current = requestCompanyId;
    const controller = beginList();

    try {
      if (!isMountedRef.current) return;
      setLoading(true);
      setError(null);
      const response = await ppeViolationsAPI.getMonthlySummary(requestCompanyId, monthlyFilters, controller.signal);
      if (controller.signal.aborted) return;
      if (isMountedRef.current && currentCompanyRef.current === requestCompanyId) {
        setMonthlyData(response.data || []);
        setMonthlyMeta(response.meta);
      }
    } catch (err: any) {
      if (controller.signal.aborted || isRequestCanceled(err)) return;
      if (isMountedRef.current && currentCompanyRef.current === requestCompanyId) {
        if (err.response?.status === 403) setPpeDisabled(true);
        else setError(err.response?.data?.message || err.message || t('loadMonthlyFailed'));
      }
    } finally {
      if (!controller.signal.aborted && isMountedRef.current && currentCompanyRef.current === requestCompanyId) {
        setLoading(false);
      }
    }
  }, [currentCompany, activeTab, monthlyFilters, canAccessPpeViolations, t, beginList]);

  const loadEmployeeHistory = useCallback(async () => {
    if (!currentCompany || !selectedEmployee) return;
    const requestCompanyId = currentCompany.company_id;
    currentCompanyRef.current = requestCompanyId;
    const controller = beginHistory();

    try {
      if (!isMountedRef.current) return;
      setHistoryLoading(true);
      
      const [year, month] = monthlyFilters.month.split('-').map(Number);
      const lastDay = new Date(year, month, 0).getDate();
      
      const params = {
        ...historyFilters,
        date_from: historyFilters.date_from || `${monthlyFilters.month}-01`,
        date_to: historyFilters.date_to || `${monthlyFilters.month}-${lastDay}`,
        per_page: 10
      };
      const response = await ppeViolationsAPI.getEmployeeViolationHistory(
        requestCompanyId, 
        selectedEmployee.id, 
        params,
        controller.signal
      );
      if (controller.signal.aborted) return;
      if (isMountedRef.current && currentCompanyRef.current === requestCompanyId) {
        setHistoryData(response.data);
        setHistoryMeta(response.meta);
      }
    } catch (err: any) {
      if (controller.signal.aborted || isRequestCanceled(err)) return;
      logError('Failed to load employee history', err);
    } finally {
      if (!controller.signal.aborted && isMountedRef.current && currentCompanyRef.current === requestCompanyId) {
        setHistoryLoading(false);
      }
    }
  }, [currentCompany, selectedEmployee, historyFilters, monthlyFilters.month, beginHistory]);

  useEffect(() => {
    if (canAccessPpeViolations) loadDropdownData();
  }, [canAccessPpeViolations, loadDropdownData]);

  useEffect(() => {
    if (activeTab === 'daily') loadDailyViolations();
    else if (activeTab === 'monthly') loadMonthlySummary();
  }, [activeTab, loadDailyViolations, loadMonthlySummary]);

  useEffect(() => {
    if (showHistoryModal) loadEmployeeHistory();
  }, [showHistoryModal, loadEmployeeHistory]);

  const handleOpenHistory = (row: PPEMonthlySummaryRow) => {
    setSelectedEmployee({ id: row.employee_id, name: row.employee_name, emp_id: row.emp_id });
    setHistoryFilters({ violation_type: '', date_from: '', date_to: '', page: 1 });
    setShowHistoryModal(true);
  };

  const handleViolationClick = (v: PPEViolation) => {
    setSelectedDetail(v);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedDetail(null);
  };

  const handleImageError = useCallback((imageUrl: string) => {
    setFailedImages(prev => new Set(prev).add(imageUrl));
  }, []);

  const [ppeTypes, setPpeTypes] = useState<string[]>([]);
  // Load ppeTypes on mount or company change
  useEffect(() => {
    if (currentCompany?.ppe_types) {
      setPpeTypes(currentCompany.ppe_types);
    }
  }, [currentCompany?.ppe_types]);

  const activePpeFields = useMemo(() => {    const fields: { key: string; label: string; confKey?: string }[] = [];
    const known = {
      hairnet_gloves: [
        { key: 'hairnet', label: tc('ppeTypes.hairnet'), confKey: 'hairnet_confidence' },
        { key: 'left_glove', label: tc('ppeTypes.lGlove'), confKey: 'left_glove_confidence' },
        { key: 'right_glove', label: tc('ppeTypes.rGlove'), confKey: 'right_glove_confidence' }
      ],
      helmet: [{ key: 'helmet', label: tc('ppeTypes.helmet'), confKey: 'helmet_confidence' }],
      industrial_gloves: [{ key: 'industrial_gloves', label: tc('ppeTypes.indGloves'), confKey: 'industrial_gloves_confidence' }],
      industrial_jacket: [{ key: 'industrial_jacket', label: tc('ppeTypes.indJacket'), confKey: 'industrial_jacket_confidence' }]
    } as Record<string, Array<{ key: string; label: string; confKey?: string }>>;

    ppeTypes.forEach(type => {
      if (known[type]) {
        fields.push(...known[type]);
      } else if (type) {
        fields.push({ key: type, label: type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) });
      }
    });

    return fields;
  }, [ppeTypes, tc]);

  const renderCompliance = (value: number | boolean | undefined, confidence?: number) => {
    if (value === undefined || value === null) return '-';
    
    // Support both boolean and 0/1 number
    const isCompliant = value === true || value === 1;
    const isNonCompliant = value === false || value === 0;

    if (!isCompliant && !isNonCompliant) return '-';

    return (
      <div className="d-flex flex-column align-items-center">
        <Badge bg={isCompliant ? 'success' : 'danger'}>
          {isCompliant ? <i className="bi bi-check-lg"></i> : <i className="bi bi-x-lg"></i>}
        </Badge>
        {confidence !== undefined && confidence !== null && (
          <small className="text-muted" style={{ fontSize: '10px' }}>
            {(confidence * 100).toFixed(0)}%
          </small>
        )}
      </div>
    );
  };

  const getViolationBadgeColor = (type: string) => {
    switch (type) {
      case 'helmet': return 'primary';
      case 'gloves': return 'info';
      case 'hairnet': return 'warning';
      case 'industrial_gloves': return 'secondary';
      case 'industrial_jacket': return 'dark';
      case 'unauthorized': return 'danger';
      default: return 'secondary';
    }
  };

  const showingLabel = tc('messages.showing', { from: '{{from}}', to: '{{to}}', total: '{{total}}', entity: '' });

  const renderViolationTable = (
    violations: PPEViolation[],
    meta: ViolationsPaginationMeta,
    onPageChange: (p: number) => void
  ) => (
    <>
      <div className="d-md-none p-3">
        {violations.map(v => {
          const pathSegments = v.image_id && currentCompany ? resolvePpeImagePathSegments({
            camera_number: v.camera_number,
            camera_name: v.camera_name,
            image_id: v.image_id
          }) : null;
          const thumbUrl = pathSegments && currentCompany ? faceImageAPI.getImageUrl(currentCompany.company_id, pathSegments.cameraSegment, pathSegments.fileSegment) : null;
          return (
            <Card key={v.id} className="mb-3 shadow-sm border an-data-card an-ppe-mobile-card an-ppe-violation-card">
              <Card.Body className="an-data-card">
                <div className="d-flex justify-content-between mb-2">
                  <div>
                    <div className="fw-bold">{v.employee_name}</div>
                    <small className="text-muted">ID: {v.emp_id}</small>
                    <div className="mt-1 d-flex gap-1 flex-wrap">
                      {v.is_vacation && <Badge bg="info">{t('vacation')}</Badge>}
                      {v.is_holiday && <Badge bg="success">{t('holiday')}</Badge>}
                    </div>
                  </div>
                  {thumbUrl && (
                    <img src={thumbUrl} alt={t('violation')} className="rounded an-ppe-thumb" style={{ width: '50px', height: '50px', objectFit: 'cover', cursor: 'pointer' }} 
                      onClick={() => handleViolationClick(v)} />
                  )}
                  <Button variant="link" size="sm" className="text-primary p-1 ms-2 text-decoration-none an-ppe-play-action" title={t('playClip')} onClick={() => { setClipViolation(v); setShowClipModal(true); }}><i className="bi bi-play-circle"></i> <span className="ms-1">{t('clip')}</span></Button>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <Badge bg={getViolationBadgeColor(v.violation_type)} className="me-1">
                      {ppeTypeLabels[v.violation_type] || v.violation_type}
                    </Badge>
                                        <Badge pill className={`an-violation-category-${v.violation_category === 'missing' ? 'missing' : 'other'} me-1`} style={{ fontSize: '10px' }}>
                      {violationCategoryLabels[v.violation_category] || v.violation_category}
                    </Badge>
                  </div>
                  <div className="text-end">
                    <small className="text-muted d-block">{formatDateTimeInCompanyTimezone(v.log_time, companyTimezone)}</small>
                    <small className="text-primary d-block">{t('conf')}: {(Number(v.confidence) * 100).toFixed(1)}%</small>
                  </div>
                </div>
                <div className="mt-2 small text-muted d-flex justify-content-between">
                  <span>{t('deptPrefix')}: {v.department_name}</span>
                  <span>{t('camPrefix')}: {v.camera_name}</span>
                </div>
              </Card.Body>
            </Card>
          );
        })}
        {violations.length === 0 && <div className="text-center py-4">{t('noViolationsFound')}</div>}
      </div>

      <div className="d-none d-md-block">
        <div className="table-responsive an-data-table an-ppe-table">
          <Table hover className="align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th>{tc('labels.employee')}</th>
                <th>{tc('labels.department')}</th>
                <th>{tc('labels.camera')}</th>
                <th>{t('violationType')}</th>
                <th>{t('category')}</th>
                <th>{t('confidence')}</th>
                <th>{t('time')}</th>
                <th>{t('image')}</th>
                <th>{t('clip')}</th>
              </tr>
            </thead>
            <tbody>
              {violations.map(v => {
                const pathSegments = v.image_id && currentCompany ? resolvePpeImagePathSegments({
                  camera_number: v.camera_number,
                  camera_name: v.camera_name,
                  image_id: v.image_id
                }) : null;
                const thumbUrl = pathSegments && currentCompany ? faceImageAPI.getImageUrl(currentCompany.company_id, pathSegments.cameraSegment, pathSegments.fileSegment) : null;
                
                return (
                  <tr key={v.id}>
                    <td>
                      <div className="fw-bold">{v.employee_name}</div>
                      <small className="text-muted">ID: {v.emp_id}</small>
                      <div className="mt-1 d-flex gap-1 flex-wrap">
                        {v.is_vacation && <Badge bg="info">{t('vacation')}</Badge>}
                        {v.is_holiday && <Badge bg="success">{t('holiday')}</Badge>}
                      </div>
                    </td>
                    <td>{v.department_name}</td>
                    <td>{v.camera_name}</td>
                    <td>
                      <Badge bg={getViolationBadgeColor(v.violation_type)}>
                        {ppeTypeLabels[v.violation_type] || v.violation_type}
                      </Badge>
                    </td>
                    <td>
                                            <Badge pill className={`an-violation-category-${v.violation_category === 'missing' ? 'missing' : 'other'}`}>
                        {violationCategoryLabels[v.violation_category] || v.violation_category}
                      </Badge>
                    </td>
                    <td className="an-numeric-cell">{(Number(v.confidence) * 100).toFixed(1)}%</td>
                    <td>{formatDateTimeInCompanyTimezone(v.log_time, companyTimezone)}</td>
                    <td>
                      {thumbUrl ? (
                        <img src={thumbUrl} alt={t('violation')} className="rounded an-ppe-thumb" style={{ width: '40px', height: '40px', objectFit: 'cover', cursor: 'pointer' }} 
                          onClick={() => handleViolationClick(v)} />
                      ) : tc('messages.na')}
                    </td>
                    <td>
                      <Button variant="link" size="sm" className="text-primary p-1 text-decoration-none an-ppe-play-action" title={t('playClip')} onClick={() => { setClipViolation(v); setShowClipModal(true); }}><i className="bi bi-play-circle"></i> <span className="ms-1">{t('clip')}</span></Button>
                    </td>
                  </tr>
                );
              })}
              {violations.length === 0 && (
                <tr><td colSpan={9} className="text-center py-4">{t('noViolationsFound')}</td></tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
      <ViolationsListPagination meta={meta} onPageChange={onPageChange} showingLabel={showingLabel} />
    </>
  );

  if (userRolePermissionsLoading || loading) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="an-enterprise-page an-page-shell an-data-page an-ppe-page">
          <div className="page-header">
            <h2 className="an-page-title">{t('title')}</h2>
          </div>
          <div className="py-5 text-center">
            <Spinner animation="border" variant="primary" />
          </div>
        </Container>
      </Layout>
    );
  }


  if (!canAccessPpeViolations) {
    return (
      <Layout title={t('title')}>
        <Container className="an-enterprise-page an-page-shell an-data-page an-ppe-page">
          <Alert variant="danger">{t('accessDenied')}</Alert>
        </Container>
      </Layout>
    );
  }

  if (ppeDisabled) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="an-enterprise-page an-page-shell an-data-page an-ppe-page">
            <div className="page-header">
              <h2 className="an-page-title">{t('title')}</h2>
            </div>
            <Alert variant="info" className="mb-4">
              <Alert.Heading>{t('ppeDisabledTitle')}</Alert.Heading>
              <p>{t('ppeDisabledMessage')}</p>
            </Alert>
          </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid className="an-enterprise-page an-page-shell an-data-page an-ppe-page">
        <div className="page-header"><h2 className="an-page-title">{t('title')}</h2></div>

        <ScopeIndicator resource="ppe_violations" />

        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'daily')} className="mb-4 an-ppe-tabs">
          <Tab eventKey="daily" title={t('dailyViolations')}>
            <Card className="mb-4 shadow-sm border-0 an-data-card an-ppe-filter-card">
              <Card.Body className="an-data-card">
                <Form onSubmit={(e) => { e.preventDefault(); setDailyFilters({ ...dailyFormState, page: 1 }); }}>
                  <Row>
                    <Col md={2} className="mb-3">
                      <Form.Label>{tc('labels.date')}</Form.Label>
                      <Form.Control type="date" value={dailyFormState.date} onChange={(e) => setDailyFormState(prev => ({...prev, date: e.target.value}))} />
                    </Col>
                    <Col md={3} className="mb-3">
                      <Form.Label>{tc('labels.department')}</Form.Label>
                      <Form.Select value={dailyFormState.department_id} onChange={(e) => setDailyFormState(prev => ({...prev, department_id: e.target.value}))} disabled={loadingDropdowns}>
                        <option value="">{tc('labels.allDepartments')}</option>
                        {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </Form.Select>
                    </Col>
                    <Col md={3} className="mb-3">
                      <Form.Label>{tc('labels.employee')}</Form.Label>
                      <EmployeeSearchPicker companyId={currentCompany?.company_id} value={dailyFormState.employee_id}
                        onChange={employee => setDailyFormState(prev => ({...prev, employee_id: employee ? String(employee.id) : ''}))}
                        placeholder={tc('labels.allEmployees')} />
                    </Col>
                    <Col md={2} className="mb-3">
                      <Form.Label>{t('violationType')}</Form.Label>
                      <Form.Select value={dailyFormState.violation_type} onChange={(e) => setDailyFormState(prev => ({...prev, violation_type: e.target.value}))}>
                        <option value="">{t('allTypes')}</option>
                        {Object.keys(ppeTypeFilterOptions).map(k => <option key={k} value={k}>{ppeTypeFilterOptions[k]}</option>)}
                      </Form.Select>
                    </Col>
                    <Col md={2} className="d-flex align-items-end mb-3">
                      <Button variant="primary" type="submit" className="w-100">{tc('buttons.apply')}</Button>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>

            <Row className="g-3 mb-4 an-ppe-summary-row">{Object.entries(dailyData.summary).filter(([k, v]) => v > 0 || k === 'total').sort((a,b) => b[1] - a[1]).map(([key, count]) => (
                <Col md={2} key={key} className="mb-3"><div className={`an-ppe-summary-card ${key === 'total' ? 'is-total' : ''}`}><div className="an-ppe-summary-label">{ppeTypeLabels[key] || key}</div><div className="an-ppe-summary-value">{count}</div></div></Col>
              ))}
            </Row>

            <Card className="shadow-sm border-0 an-data-card an-ppe-results-card">
              <Card.Body className="p-0 an-data-card">
                <div className="an-ppe-cooldown-bar">
                  <Form.Check
                    type="switch"
                    id="cooldown-toggle"
                    label={t('cooldownLabel')}
                    checked={cooldownEnabled}
                    onChange={() => setCooldownEnabled(prev => !prev)}
                  />
                  {cooldownEnabled && dailyData.violations.length > 0 && (
                    <small className="text-muted">
                      {t('showingOfTotal', { shown: applyCooldown(dailyData.violations).length, total: dailyData.violations.length })}
                    </small>
                  )}
                </div>
                {renderViolationTable(
                  cooldownEnabled ? applyCooldown(dailyData.violations) : dailyData.violations,
                  dailyMeta,
                  (p) => setDailyFilters(prev => ({...prev, page: p}))
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="monthly" title={t('monthlySummary')}>
            <Card className="mb-4 shadow-sm border-0 an-data-card an-ppe-filter-card">
              <Card.Body className="an-data-card">
                <Form onSubmit={(e) => { e.preventDefault(); setMonthlyFilters({ ...monthlyFormState, page: 1 }); }}>
                  <Row>
                    <Col md={3} className="mb-3">
                      <Form.Label>{t('month')}</Form.Label>
                      <Form.Control type="month" value={monthlyFormState.month} onChange={(e) => setMonthlyFormState(prev => ({...prev, month: e.target.value}))} />
                    </Col>
                    <Col md={4} className="mb-3">
                      <Form.Label>{tc('labels.department')}</Form.Label>
                      <Form.Select value={monthlyFormState.department_id} onChange={(e) => setMonthlyFormState(prev => ({...prev, department_id: e.target.value}))} disabled={loadingDropdowns}>
                        <option value="">{tc('labels.allDepartments')}</option>
                        {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </Form.Select>
                    </Col>
                    <Col md={2} className="d-flex align-items-end mb-3">
                      <Button variant="primary" type="submit" className="w-100">{tc('buttons.apply')}</Button>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>

            <Card className="shadow-sm border-0 an-data-card an-ppe-results-card">
              <Card.Body className="p-0 an-data-card">
                    <div className="table-responsive an-data-table an-ppe-table">
                      <Table hover className="align-middle mb-0 text-center">
                        <thead className="bg-light">
                          <tr>
                            <th className="text-start">{t('employeeName')}</th>
                            <th>{t('employeeId')}</th>
                            <th>{tc('labels.department')}</th>
                            <th>{t('totalColumn')}</th>
                            <th>{ppeTypeLabels.helmet}</th>
                            <th>{ppeTypeLabels.gloves}</th>
                            <th>{ppeTypeLabels.hairnet}</th>
                            <th>{ppeTypeLabels.industrial_gloves}</th>
                            <th>{ppeTypeLabels.industrial_jacket}</th>
                            <th>{ppeTypeLabels.unauthorized}</th>
                            <th>{t('action')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {monthlyData.map((row) => (
                            <tr key={row.emp_id}>
                              <td className="text-start fw-bold">{row.employee_name}</td>
                              <td>{row.emp_id}</td>
                              <td>{row.department_name}</td>
                              <td><Badge bg="primary">{row.total}</Badge></td>
                              <td>{row.helmet || 0}</td>
                              <td>{row.gloves || 0}</td>
                              <td>{row.hairnet || 0}</td>
                              <td>{row.industrial_gloves || 0}</td>
                              <td>{row.industrial_jacket || 0}</td>
                              <td>{row.unauthorized || 0}</td>
                              <td>
                                <Button variant="link" size="sm" className="text-primary p-1 text-decoration-none" onClick={() => handleOpenHistory(row)}><i className="bi bi-eye"></i> <span className="ms-1">{tc('buttons.viewDetails')}</span></Button>
                              </td>
                            </tr>
                          ))}
                          {monthlyData.length === 0 && (
                            <tr><td colSpan={11} className="text-center py-4">{t('noSummaryData')}</td></tr>
                          )}
                        </tbody>
                      </Table>
                    </div>

                    <ViolationsListPagination
                      meta={monthlyMeta}
                      onPageChange={(p) => setMonthlyFilters(prev => ({ ...prev, page: p }))}
                      showingLabel={showingLabel}
                    />
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>

        <ViolationClipModal
          show={showClipModal}
          onHide={() => setShowClipModal(false)}
          violation={clipViolation}
          companyId={currentCompany?.company_id ?? ''}
          companyTimezone={companyTimezone}
        />

        <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="xl" dialogClassName="an-ppe-modal">
          <Modal.Header closeButton className="an-section-divider">
            <Modal.Title>{t('violationHistory')} - {selectedEmployee?.name} - {monthlyFilters.month}</Modal.Title>
          </Modal.Header>
          <Modal.Body>            
            <Row className="mb-3">
              <Col md={3}>
                <Form.Select size="sm" value={historyFilters.violation_type} onChange={(e) => setHistoryFilters(prev => ({...prev, violation_type: e.target.value, page: 1}))}>
                  <option value="">{t('allTypes')}</option>
                  {Object.keys(ppeTypeFilterOptions).map(k => <option key={k} value={k}>{ppeTypeFilterOptions[k]}</option>)}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Control size="sm" type="date" value={historyFilters.date_from} onChange={(e) => setHistoryFilters(prev => ({...prev, date_from: e.target.value, page: 1}))} placeholder={tc('labels.fromDate')} />
              </Col>
              <Col md={3}>
                <Form.Control size="sm" type="date" value={historyFilters.date_to} onChange={(e) => setHistoryFilters(prev => ({...prev, date_to: e.target.value, page: 1}))} placeholder={tc('labels.toDate')} />
              </Col>
            </Row>

            {historyLoading ? (
              <div className="text-center py-4"><Spinner animation="border" /></div>
            ) : (
              <>
                <div className="d-flex flex-wrap gap-2">
                  {Object.entries(historyData.summary).filter(([_, v]) => v > 0).map(([key, count]) => (
                    <Badge key={key} bg={key === 'total' ? 'primary' : 'info'} className="p-2">
                      {ppeTypeLabels[key] || key}: {count}
                    </Badge>
                  ))}
                </div>
                {renderViolationTable(historyData.violations, historyMeta, (p) => setHistoryFilters(prev => ({...prev, page: p})))}
              </>
            )}
          </Modal.Body>
        </Modal>

        <Modal 
          show={showDetailModal} 
          onHide={handleCloseDetailModal} 
          size="lg" 
          centered
        dialogClassName="an-ppe-modal">
          <Modal.Header closeButton className="an-section-divider">
            <Modal.Title>{t('modalTitle', { employee: selectedDetail?.employee_name })}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            {selectedDetail && (() => {
              const companyId = currentCompany?.company_id ?? '';
              const segments = resolvePpeImagePathSegments(selectedDetail);
              const imageUrl =
                companyId && segments
                  ? faceImageAPI.getImageUrl(companyId, segments.cameraSegment, segments.fileSegment)
                  : null;
              const isModalImageFailed = imageUrl ? failedImages.has(imageUrl) : false;

              return (
                <div className="image-container">
                  <div className="position-relative w-100">
                    <div className="d-flex justify-content-center w-100">
                      {imageUrl && !isModalImageFailed && selectedDetail.coordinates ? (
                        <ImageWithDetectionBox
                          src={imageUrl}
                          coordinates={selectedDetail.coordinates}
                          alt={`${selectedDetail.employee_name} detection`}
                          className="img-fluid w-100"
                          style={{ maxHeight: '70vh', width: '100%' }}
                          showDetectionBox={false}
                          showEmployeeName={true}
                          employeeName={selectedDetail.employee_name}
                          onError={() => handleImageError(imageUrl)}
                        />
                      ) : (
                        <div className="text-center p-5">
                          <i className="bi bi-image mb-3" style={{ fontSize: '3rem', color: 'var(--an-border)' }}></i>
                          <p className="text-muted">{tc('messages.na')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="p-3 an-modal-detail-strip reports-modal-details">
                    <Row>
                      <Col md={6}>
                        <div className="mb-2">
                          <strong>{tc('labels.employee')}</strong> {selectedDetail.employee_name || tc('messages.na')} ({selectedDetail.emp_id || tc('messages.na')})
                          <div className="mt-1 d-flex gap-1 flex-wrap">
                            {selectedDetail.is_vacation && <Badge bg="info">{t('vacation')}</Badge>}
                            {selectedDetail.is_holiday && <Badge bg="success">{t('holiday')}</Badge>}
                          </div>
                        </div>
                        <div className="mb-2">
                          <strong>{tc('labels.department')}</strong> {selectedDetail.department_name || tc('messages.na')}
                        </div>
                        <div className="mb-2">
                          <strong>{tc('labels.camera')}</strong> {selectedDetail.camera_name || tc('messages.na')} ({selectedDetail.camera_number || tc('messages.na')})
                        </div>
                        {selectedDetail.coordinates && (
                          <div className="mb-2">
                            <strong>{t('modalCoordinates')}</strong>
                            <br />
                            <span className="coordinates-text">
                              ({selectedDetail.coordinates.x1}, {selectedDetail.coordinates.y1}) {'->'} ({selectedDetail.coordinates.x2}, {selectedDetail.coordinates.y2})
                            </span>
                          </div>
                        )}
                      </Col>
                      <Col md={6}>
                        <div className="mb-2">
                          <strong>{t('time')}</strong> {formatDateTimeInCompanyTimezone(selectedDetail.log_time, companyTimezone)}
                        </div>
                        {selectedDetail.model_setup && (
                          <div className="mb-3">
                            <strong>{t('modalModelSetup')}</strong> {selectedDetail.model_setup}
                          </div>
                        )}

                        <h6 className="fw-bold mb-2">{t('ppeCompliance')}</h6>
                        <div className="d-flex flex-wrap gap-2">
                          {activePpeFields.map(field => (
                            <div key={field.key} className="d-flex flex-column align-items-center p-2 an-panel-soft" style={{ minWidth: '70px' }}>
                              <small className="text-muted mb-1 text-center" style={{ fontSize: '0.65rem' }}>{field.label}</small>
                              {renderCompliance(selectedDetail[field.key], field.confKey ? selectedDetail[field.confKey] : undefined)}
                            </div>
                          ))}
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
              );
            })()}
          </Modal.Body>
        </Modal>
      </Container>
    </Layout>
  );
};

export default PPEViolations;







